import React, { useEffect, useRef, useState } from "react";
import { streamChat } from "../lib/api";
import { Send, Sparkles } from "lucide-react";
import { useLang } from "../lib/i18n";
import VoicePlayButton from "../components/VoicePlayButton";
import { getStoredProfile } from "../components/ChildProfileModal";
import { track } from "../lib/ga";

function sid() {
  let s = sessionStorage.getItem("aurelia_sid");
  if (!s) { s = "p-" + Math.random().toString(36).slice(2, 10); sessionStorage.setItem("aurelia_sid", s); }
  return s;
}

export default function Assistante() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([{ role: "assistant", content: t("ask.hello") }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(sid());
  const scroller = useRef(null);

  // Reset greeting when language changes AND no user messages sent yet
  useEffect(() => {
    setMessages((m) => (m.length <= 1 ? [{ role: "assistant", content: t("ask.hello") }] : m));
     
  }, [lang]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const SUGGESTIONS = [1, 2, 3, 4, 5].map((i) => t(`ask.sug.${i}`));

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    track.chatMessage(lang);
    await streamChat({
      sessionId: sessionId.current,
      message: msg,
      lang,
      childProfile: getStoredProfile(),
      onDelta: (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + chunk };
          return copy;
        });
      },
      onDone: () => setBusy(false),
      onError: () => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: t("ask.error") };
          return copy;
        });
        setBusy(false);
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="assistante-page">
      <header>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F4F1DE] text-[#3D405B] text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> {t("ask.badge")}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B] mt-3">{t("ask.title")}</h1>
        <p className="text-[#8A8D91] mt-2 max-w-2xl">{t("ask.subtitle")}</p>
      </header>

      <div className="bg-white border border-[#E8E6E1] rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col h-[60vh] min-h-[480px] overflow-hidden">
        <div ref={scroller} className="flex-1 overflow-y-auto p-5 md:p-8 space-y-5" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed relative group ${
                m.role === "user"
                  ? "bg-[#3D405B] text-white rounded-br-md"
                  : "bg-[#F4F1DE] text-[#3D405B] rounded-bl-md"
              }`} data-testid={`msg-${m.role}-${i}`}>
                {m.content || (busy && i === messages.length - 1 ? <span className="opacity-60">{t("ask.thinking")}</span> : null)}
                {m.role === "assistant" && m.content && !busy && (
                  <div className="mt-2 flex justify-end">
                    <VoicePlayButton text={m.content} lang={lang} testId={`voice-play-${i}`} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="px-5 md:px-8 pb-3 flex flex-wrap gap-2" data-testid="chat-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                data-testid={`suggestion-${i}`}
                onClick={() => send(s)}
                className="text-xs px-3 py-1.5 rounded-full border border-[#E8E6E1] bg-white hover:border-[#81B29A] hover:bg-[#81B29A]/10 text-[#3D405B] transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#E8E6E1] p-3 md:p-4 flex gap-2"
          data-testid="chat-form"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("ask.input.placeholder")}
            data-testid="chat-input"
            className="flex-1 bg-[#FDFBF7] border border-[#E8E6E1] rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            data-testid="chat-send"
            className="bg-[#E07A5F] text-white rounded-full px-5 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> {t("ask.send")}
          </button>
        </form>
      </div>
    </div>
  );
}
